import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, CalendarDays, Newspaper } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api, fileUrl } from '../lib/api';
import { PageHeader, SectionHeader, Loading, EmptyState, formatDate, NEWS_CATEGORIES } from '../components/Shared';

const WATCH_PILLARS = [
  { title: 'Decisions in plain language', body: 'What Council voted on, what it costs, and what it means for Ward 8, without the jargon.' },
  { title: 'Follow the money', body: 'Budgets, contracts, and spending decisions residents deserve to understand.' },
  { title: 'Follow-up, on the record', body: 'When answers are promised, City Hall Watch tracks whether they actually arrive.' },
];

export default function CityHallWatch() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/news?category=city_hall').then((r) => r.data || []),
      api.get('/news?category=accountability').then((r) => r.data || []),
    ]).then((results) => {
      const merged = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
      const unique = [...new Map(merged.map((p) => [p.id, p])).values()];
      unique.sort((a, b) => (b.published_at || '').localeCompare(a.published_at || ''));
      setStories(unique);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader kicker="THE KAWARTHA PEOPLE'S DESK" title="City Hall Watch" subtitle="Accountability journalism for Kawartha Lakes: decisions, spending, and follow-up, explained in plain language." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* What this section does */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10" data-testid="cityhallwatch-pillars">
          {WATCH_PILLARS.map((p) => (
            <Card key={p.title} className="card-lift">
              <CardContent className="p-5">
                <Landmark className="h-6 w-6 text-[#C9A84C] mb-2" />
                <h3 className="font-serif font-bold text-[#1a2744] leading-snug">{p.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{p.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <SectionHeader kicker="LATEST" title="City Hall Watch stories" />
        {loading ? <Loading /> : stories.length === 0 ? (
          <EmptyState title="No City Hall Watch stories yet" subtitle="Stories about Council decisions and accountability will appear here as they are published." />
        ) : (
          <div className="space-y-4" data-testid="cityhallwatch-stories">
            {stories.map((post) => (
              <Link key={post.id} to={`/newsroom/${post.slug}`} className="block group" data-testid="cityhallwatch-story-card">
                <Card className="card-lift">
                  <CardContent className="p-4 sm:p-5 flex gap-4">
                    {post.image_url && <img src={fileUrl(post.image_url)} alt="" className="h-20 w-24 sm:h-24 sm:w-32 object-cover rounded-lg shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A84C]">{NEWS_CATEGORIES[post.category] || post.category}</p>
                      <p className="font-serif font-bold text-[#1a2744] leading-snug mt-0.5 group-hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4">{post.title}</p>
                      {post.dek && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{post.dek}</p>}
                      <p className="text-xs text-slate-500 mt-1.5">{post.author || 'Campaign Team'} · {formatDate(post.published_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Cross links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10">
          <Card className="border-[#C9A84C]">
            <CardContent className="p-6">
              <CalendarDays className="h-7 w-7 text-[#1a2744] mb-2" />
              <h3 className="font-serif font-black text-[#1a2744]">Watch decisions happen live</h3>
              <p className="text-sm text-slate-600 mt-1.5">Upcoming Council meetings, agendas, and how to participate.</p>
              <Button asChild className="mt-4 h-12 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="cityhallwatch-meetings-cta"><Link to="/meetings">Council Meetings</Link></Button>
            </CardContent>
          </Card>
          <Card className="border-[#C9A84C]">
            <CardContent className="p-6">
              <Newspaper className="h-7 w-7 text-[#1a2744] mb-2" />
              <h3 className="font-serif font-black text-[#1a2744]">All Ward 8 news</h3>
              <p className="text-sm text-slate-600 mt-1.5">Every section of the People's Desk newsroom in one place.</p>
              <Button asChild variant="outline" className="mt-4 h-12 w-full border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="cityhallwatch-newsroom-cta"><Link to="/newsroom">Visit the Newsroom</Link></Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
